/**
 * Business limits for coaching reviews. Every value lives in the Firestore doc
 * `config/coaching`; these are the defaults a missing or malformed field falls
 * back to. Pure: the server reads the doc, the app reads the doc, both parse
 * it here so they cannot disagree about a clamp.
 */

export interface CoachingConfig {
    enabled: boolean;
    /**
     * Whether Send takes payment. Off, a request is sent free as before; on, the
     * player buys the coach's price tier in the store first. Stays off until the
     * three store products exist.
     */
    paymentsEnabled: boolean;
    /** Clubs the player entry point is shown at. Empty means no club, bar testers. */
    pilotClubIds: string[];
    /** Accounts that see coaching at every club with coaches, whatever the pilot list. */
    testerUids: string[];
    /** Shortest window a player may add, before snapping. */
    minClipSec: number;
    /** Most clips in one bucket. */
    maxClips: number;
    /** Longest a bucket may run in total, snapped. */
    maxBucketSec: number;
    /** How long a draft bucket lives before the sweep clears it. */
    draftDays: number;
    maxOpenPerPlayer: number;
    defaultCoachMaxOpen: number;
    /** What a coach promises when they have not set their own: working days to return a review. */
    defaultTurnaroundDays: number;
    /** The most working days a coach may promise. */
    maxTurnaroundDays: number;
    /** The coach must accept inside this, counted from their first working moment after sending. */
    acceptWithinHours: number;
    /** Coach reminder this long after sending if still unanswered. 0 disables; must be inside the accept window. */
    acceptReminderAfterHours: number;
    /** Coach reminder this long before the review is due. 0 disables. */
    dueReminderBeforeHours: number;
    /** What players read as the price of one review, in pence. PLACEHOLDER until Jack sets the figure. */
    priceDisplayPence: number;
    /** Clips, moments and the record are deleted this long after close. */
    retentionDays: number;
    /** Lifetime of a playback signature. */
    signedUrlTtlSec: number;
    maxNoteChars: number;
    maxSummaryChars: number;
    maxMomentNoteChars: number;
    maxMomentsPerReview: number;
    maxShapesPerMoment: number;
    maxPointsPerPath: number;
    /** Segment length the client picker snaps to before the server does it for real. */
    segmentSec: number;
    /** Recordings one request may draw on. The stepper is built for 1. */
    maxRecordingsPerRequest: number;
    /** Length of "Add this moment" before the player adjusts it. */
    defaultClipSec: number;
    /** "Pick for me" stops at this many clips and this many seconds, inside the bucket limits. */
    pickMaxClips: number;
    pickMaxSec: number;
    /** Clip length and the stretch skipped at each end when a match has no rally data. */
    pickSpreadClipSec: number;
    pickSkipEdgeSec: number;
    /** A match with fewer days of retention left than this is not offered. */
    minSourceDaysLeft: number;
    /** A recording shorter than this is not offered or accepted: a test start or a mis-tap, not a match. */
    minMatchSec: number;
    /** Days a request that closed without a review keeps its clips and record. Never longer than `retentionDays`. */
    unreviewedRetentionDays: number;
    /** Longest voice note a coach can record, per moment or for the summary. */
    maxVoiceSec: number;
    /** Rewrites a coach may ask Improve for in a day. 0 turns Improve off. */
    maxImprovesPerDay: number;
    /** The store's fee on a review, in basis points: an estimate until the store's monthly report gives the real one. */
    storeFeeBps: number;
    /** Swana's share of what is left after the store fee, in basis points; the club gets the rest. */
    platformFeeBps: number;
    /** Days after a review is returned before the club's share is paid, so a late refund never follows a payout. */
    payoutHoldDays: number;
}

export const DEFAULT_COACHING_CONFIG: CoachingConfig = {
    enabled: false,
    paymentsEnabled: false,
    pilotClubIds: [],
    testerUids: [],
    minClipSec: 10,
    maxClips: 12,
    maxBucketSec: 15 * 60,
    draftDays: 14,
    maxOpenPerPlayer: 3,
    defaultCoachMaxOpen: 10,
    defaultTurnaroundDays: 3,
    maxTurnaroundDays: 7,
    acceptWithinHours: 24,
    acceptReminderAfterHours: 12,
    dueReminderBeforeHours: 24,
    // The price of a coach who has not chosen a tier, at a club that has not set one. A store tier, so it can be bought.
    priceDisplayPence: 1499,
    retentionDays: 180,
    signedUrlTtlSec: 2 * 60 * 60,
    maxNoteChars: 750,
    maxSummaryChars: 4000,
    maxMomentNoteChars: 1000,
    maxMomentsPerReview: 60,
    maxShapesPerMoment: 40,
    maxPointsPerPath: 600,
    segmentSec: 4,
    maxRecordingsPerRequest: 1,
    defaultClipSec: 12,
    pickMaxClips: 8,
    pickMaxSec: 10 * 60,
    pickSpreadClipSec: 45,
    pickSkipEdgeSec: 120,
    minSourceDaysLeft: 2,
    minMatchSec: 180,
    unreviewedRetentionDays: 14,
    maxVoiceSec: 120,
    maxImprovesPerDay: 100,
    storeFeeBps: 1500,
    platformFeeBps: 1500,
    payoutHoldDays: 14,
};

type NumericKey = {
    [K in keyof CoachingConfig]: CoachingConfig[K] extends number ? K : never
}[keyof CoachingConfig];

const NUMERIC_BOUNDS: Record<NumericKey, [min: number, max: number]> = {
    minClipSec: [1, 60 * 60],
    maxClips: [1, 50],
    maxBucketSec: [60, 60 * 60],
    draftDays: [1, 365],
    maxOpenPerPlayer: [1, 100],
    defaultCoachMaxOpen: [1, 1000],
    defaultTurnaroundDays: [1, 30],
    maxTurnaroundDays: [1, 30],
    acceptWithinHours: [1, 24 * 30],
    acceptReminderAfterHours: [0, 24 * 30],
    dueReminderBeforeHours: [0, 24 * 30],
    priceDisplayPence: [0, 100_000],
    retentionDays: [1, 3650],
    signedUrlTtlSec: [60, 24 * 60 * 60],
    maxNoteChars: [0, 20000],
    maxSummaryChars: [0, 50000],
    maxMomentNoteChars: [0, 20000],
    maxMomentsPerReview: [1, 500],
    maxShapesPerMoment: [1, 500],
    maxPointsPerPath: [2, 5000],
    segmentSec: [1, 60],
    maxRecordingsPerRequest: [1, 50],
    defaultClipSec: [5, 600],
    pickMaxClips: [1, 50],
    pickMaxSec: [60, 60 * 60],
    pickSpreadClipSec: [5, 600],
    pickSkipEdgeSec: [0, 60 * 60],
    minSourceDaysLeft: [0, 365],
    minMatchSec: [0, 60 * 60],
    unreviewedRetentionDays: [1, 3650],
    maxVoiceSec: [10, 600],
    maxImprovesPerDay: [0, 1000],
    storeFeeBps: [0, 5000],
    platformFeeBps: [0, 10_000],
    payoutHoldDays: [0, 90],
};

function numberIn(raw: unknown, fallback: number, [min, max]: [number, number]): number {
    if (typeof raw !== "number" || !Number.isFinite(raw)) return fallback;
    return Math.min(max, Math.max(min, raw));
}

function stringList(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    return raw.filter((v): v is string => typeof v === "string" && v.length > 0);
}

/** Parse the raw `config/coaching` doc. Never throws: a bad field takes its default. */
export function parseCoachingConfig(raw: unknown): CoachingConfig {
    const d = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    const cfg: CoachingConfig = {
        ...DEFAULT_COACHING_CONFIG,
        enabled: d.enabled === true,
        paymentsEnabled: d.paymentsEnabled === true,
        pilotClubIds: stringList(d.pilotClubIds),
        testerUids: stringList(d.testerUids),
    };
    for (const key of Object.keys(NUMERIC_BOUNDS) as NumericKey[]) {
        cfg[key] = numberIn(d[key], DEFAULT_COACHING_CONFIG[key], NUMERIC_BOUNDS[key]);
    }
    if (cfg.minClipSec > cfg.maxBucketSec) cfg.minClipSec = cfg.maxBucketSec;
    if (cfg.defaultClipSec < cfg.minClipSec) cfg.defaultClipSec = cfg.minClipSec;
    if (cfg.defaultTurnaroundDays > cfg.maxTurnaroundDays) cfg.defaultTurnaroundDays = cfg.maxTurnaroundDays;
    if (cfg.acceptReminderAfterHours >= cfg.acceptWithinHours) cfg.acceptReminderAfterHours = 0;
    return cfg;
}

export function isCoachingTester(cfg: CoachingConfig, uid: string | null | undefined): boolean {
    return !!uid && cfg.testerUids.includes(uid);
}

/**
 * Whether coaching is open to this player for a recording made at this club: the
 * club is on the pilot list, or the player is a tester. The same rule gates the
 * app's entry points and the server's draft callables.
 */
export function isCoachingOpenAtClub(cfg: CoachingConfig, clubId: string | null | undefined, uid?: string | null): boolean {
    if (!cfg.enabled || !clubId) return false;
    return cfg.pilotClubIds.includes(clubId) || isCoachingTester(cfg, uid);
}

/**
 * Whether the Coaching tab shows for a player: members of a pilot club, testers, and
 * anyone already holding a submission, who keeps it whatever their clubs because a
 * clip can be sent from any recording made at a pilot club, member or not.
 */
export function isCoachingTabVisible(cfg: CoachingConfig, memberClubIds: readonly string[], hasSubmissions: boolean, uid?: string | null): boolean {
    if (!cfg.enabled) return false;
    if (hasSubmissions || isCoachingTester(cfg, uid)) return true;
    return memberClubIds.some((id) => cfg.pilotClubIds.includes(id));
}

/** "3 days" or "36 hours": the promise in the player's words. */
export function promiseText(hours: number): string {
    if (hours % 24 === 0 && hours >= 24) { const d = hours / 24; return d === 1 ? "1 day" : `${d} days`; }
    return hours === 1 ? "1 hour" : `${hours} hours`;
}

/** "£15" or "£12.50". */
export function priceText(pence: number): string {
    const pounds = Math.floor(pence / 100);
    const rest = pence % 100;
    return rest === 0 ? `£${pounds}` : `£${pounds}.${String(rest).padStart(2, "0")}`;
}
